// GA4 property for the buy box events (see track()).
const GA4_MEASUREMENT_ID = "G-7LJV1QWL3J";

class ProductFormSubmit extends HTMLElement {
  constructor() {
    super();

    this.style.display = "block";

    const elements = {
      form: this.closest("form"),
      button: this.querySelector("button"),
    };

    this.elements = elements;
    this.loadingTargets = [];

    elements.selling_plan = elements.form.querySelector("[name=selling_plan]");

    elements.form.addEventListener("submit", (event) => {
      const submitter = event.submitter;
      if (!this.ownsSubmit(submitter)) return;
      event.preventDefault();
      this.handleSubmit(submitter);
    });

    // Restoring from bfcache (Back from checkout) keeps the success state; clear it.
    window.addEventListener("pageshow", (event) => {
      if (event.persisted) this.resetState();
    });
  }

  // Every instance in a form hears the same submit event; exactly one handles
  // it. The instance wrapping the submitter owns it (so the one-time button's
  // data-clear-selling-plan applies). A submitter outside any instance (Enter
  // key, button[form]) goes to the form's first primary instance.
  ownsSubmit(submitter) {
    const owner = submitter?.closest?.("product-form-submit");
    if (owner) return owner === this;

    const instances = [
      ...this.elements.form.querySelectorAll("product-form-submit"),
    ];
    const primary =
      instances.find((el) => el.dataset.clearSellingPlan !== "true") ||
      instances[0];
    return primary === this;
  }

  getLoadingTargets(submitter) {
    const targets = new Set();
    const { button, form } = this.elements;

    if (button) targets.add(button);
    if (submitter instanceof HTMLElement) targets.add(submitter);

    // getAttribute: `form.id` resolves to the form's <input name="id">.
    const formId = form?.getAttribute("id");
    if (formId) {
      document
        .querySelectorAll(
          `button[type="submit"][form="${CSS.escape(formId)}"]`,
        )
        .forEach((el) => targets.add(el));
    }

    return [...targets];
  }

  setLoading(isLoading) {
    this.loadingTargets.forEach((el) => {
      el.classList.toggle("state--loading", isLoading);
      el.setAttribute("aria-busy", isLoading ? "true" : "false");
    });
  }

  setSuccess(label) {
    clearTimeout(this.successTimeout);
    this.loadingTargets.forEach((el) => {
      el.classList.remove("state--loading");
      el.classList.add("state--success");
      el.setAttribute("aria-busy", "false");
      el.dataset.successLabel = label;
    });
    this.announce(label);
  }

  resetState() {
    clearTimeout(this.successTimeout);
    this.loadingTargets.forEach((el) => {
      el.classList.remove("state--loading", "state--success");
      el.setAttribute("aria-busy", "false");
      delete el.dataset.successLabel;
    });
  }

  // Main-item failures (sold out, selling plan, network) must be visible: on
  // checkout-action pages the cart was already cleared before the add ran.
  showError(message) {
    if (!this.errorMessage) {
      this.errorMessage = document.createElement("p");
      this.errorMessage.className = "product-form-submit-error font-1 size--1";
      this.errorMessage.setAttribute("role", "alert");
    }
    this.errorMessage.textContent = message;
    // Re-append so assistive tech announces it again on repeat failures.
    this.appendChild(this.errorMessage);
  }

  clearError() {
    this.errorMessage?.remove();
  }

  announce(message) {
    if (!this.statusRegion) {
      this.statusRegion = document.createElement("span");
      this.statusRegion.className = "sr-only";
      this.statusRegion.setAttribute("role", "status");
      this.appendChild(this.statusRegion);
    }
    this.statusRegion.textContent = message;
  }

  handleSubmit(submitter) {
    const { button, form, selling_plan } = this.elements;
    if (!button || button.disabled) return;

    this.loadingTargets = this.getLoadingTargets(submitter);
    if (
      this.loadingTargets.some(
        (el) =>
          el.classList.contains("state--loading") ||
          el.classList.contains("state--success"),
      )
    )
      return;

    this.clearError();
    this.setLoading(true);

    const data = new FormData(form);

    if (selling_plan && selling_plan.disabled) {
      data.set("selling_plan", "");
    }

    if (this.dataset.clearSellingPlan === "true") {
      data.set("selling_plan", "");
    }

    const sellingPlan = data.get("selling_plan");
    const redirectToCheckout = this.dataset.redirectToCheckout === "true";
    const mainItem = this.formDataToItem(data);
    const giftItems = sellingPlan ? this.subscriptionGiftItems() : [];
    // The auto-apply code only affects subscription line items; skip it
    // for one-time adds so the code can't ride along on non-subscription carts.
    const discountCode = sellingPlan ? form.dataset.autoDiscountCode : null;

    // Shared by every GA4 event of this submit (see track()).
    this.trackingContext = {
      purchase_type: sellingPlan ? "subscription" : "one_time",
      variant_id: String(mainItem.id),
      selling_plan_id: sellingPlan ? String(sellingPlan) : undefined,
      gift_count: giftItems?.length || 0,
      discount_code: discountCode || undefined,
      page_view_name:
        new URLSearchParams(window.location.search).get("view") || undefined,
    };
    this.track("buy_box_add_to_cart_start");

    let stage = redirectToCheckout ? "clear_cart" : "add_to_cart";
    const prepareCart = redirectToCheckout
      ? this.clearCart()
      : Promise.resolve();

    prepareCart
      .then(() => {
        stage = "add_to_cart";
        return this.addItems(mainItem, giftItems);
      })
      .then(async ({ cartResponse, subscriptionGiftError, giftsRetried }) => {
        let cart = cartResponse;
        const failedGiftCount = subscriptionGiftError?.failedGifts?.length || 0;

        this.track("buy_box_add_to_cart_success", {
          gifts_added: giftItems.length - failedGiftCount,
          gifts_failed: failedGiftCount,
          gifts_retried: Boolean(giftsRetried),
        });

        // Apply discount code to the cart
        if (discountCode) {
          const discountResponse = await this.applyDiscount(discountCode).catch(
            (error) => {
              console.error("ProductFormSubmitDiscountError: ", error);
              this.track("buy_box_discount", {
                discount_status: "error",
                error_message: error?.message,
              });
            },
          );

          if (discountResponse) {
            cart = discountResponse;
            this.track("buy_box_discount", {
              discount_status: this.discountStatus(discountResponse, discountCode),
            });
          }
        }

        // Never silent-checkout when the promised gift failed to add.
        if (redirectToCheckout && !subscriptionGiftError) {
          // Checkout takes a moment to load; confirm the add instead of spinning.
          // Success stays until navigation so the button can't be re-submitted.
          this.setSuccess(
            this.dataset.checkoutSuccessLabel || "Added to cart…",
          );
          this.track("buy_box_checkout_redirect");
          window.location.href = this.checkoutUrl(discountCode);
          return;
        }

        this.setSuccess(this.dataset.successLabel || "Added to cart");

        document.dispatchEvent(
          new CustomEvent("addtocart", {
            detail: {
              ...(cart && typeof cart === "object" ? cart : {}),
              subscriptionGiftError: subscriptionGiftError || null,
            },
          }),
        );
        this.successTimeout = setTimeout(() => this.resetState(), 2000);
      })
      .catch((error) => {
        console.error("ProductFormSubmitError: ", error);
        this.track("buy_box_add_to_cart_error", {
          stage,
          error_status: error?.status,
          error_message: error?.message,
        });
        this.resetState();
        // Customer-facing copy comes from Liquid so it follows the page language;
        // Shopify's technical description stays in the console above.
        this.showError(
          this.dataset.errorLabel ||
            window.Theme?.Strings?.addToCartError ||
            "",
        );
      });
  }

  /**
   * Adds the items to the cart
   * This function adds the main item and gifts
   *
   * 1. The main item and gifts in one request (the fast path)
   * 2. If Shopify rejects the batch (4xx), retry sequentially:
   *   2.1. Main item first (its failure fails the whole add), then each gift on its own, so one unavailable gift can't drop the others.
   *   2.2. Gift failures are surfaced without blocking the sale.
   *   2.3. Sequential on purpose: concurrent /cart/add.js calls can overwrite each other.
   */
  addItems(mainItem, giftItems) {
    if (!giftItems.length) {
      return this.addToCart([mainItem]).then((cartResponse) => ({
        cartResponse,
      }));
    }

    return this.addToCart([mainItem, ...giftItems])
      .then((cartResponse) => ({ cartResponse }))
      .catch(async (error) => {
        if (!(error.status >= 400 && error.status < 500)) throw error;

        const cartResponse = await this.addToCart([mainItem]);

        const failedGifts = [];
        const giftErrors = [];
        for (const giftItem of giftItems) {
          try {
            await this.addToCart([giftItem]);
          } catch (giftError) {
            failedGifts.push(giftItem);
            giftErrors.push(giftError?.message || String(giftError));
          }
        }

        if (!failedGifts.length) return { cartResponse, giftsRetried: true };

        const detail = giftErrors.join(" | ");
        this.reportGiftAddFailure(new Error(detail), failedGifts);
        return {
          cartResponse,
          giftsRetried: true,
          subscriptionGiftError: {
            message: window.Theme?.Strings?.subscriptionGiftError,
            detail,
            failedGifts,
          },
        };
      });
  }

  // Shopify's shareable discount link applies the code to the cart and then
  // redirects to checkout. Same link as the new site's buy box
  // (assets/ev-checkout-url.js).
  checkoutUrl(discountCode) {
    const root = window.Shopify?.routes?.root || "/";
    const locale = this.dataset.checkoutLocale;
    const checkout = `${root}checkout${locale ? `?locale=${encodeURIComponent(locale)}` : ""}`;
    if (!discountCode) return checkout;
    return `${root}discount/${encodeURIComponent(discountCode)}?redirect=${encodeURIComponent(checkout)}`;
  }

  formDataToItem(data) {
    const item = {
      id: Number(data.get("id")),
      quantity: Number(data.get("quantity") || 1),
    };

    const sellingPlan = data.get("selling_plan");
    if (sellingPlan) item.selling_plan = Number(sellingPlan);

    const properties = {};
    for (const [key, value] of data.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && typeof value === "string" && value !== "")
        properties[match[1]] = value;
    }
    if (Object.keys(properties).length) item.properties = properties;

    return item;
  }

  reportGiftAddFailure(error, giftItems) {
    const payload = {
      event: "subscription_gift_add_failed",
      message: error?.message || String(error),
      giftItems,
    };

    console.error("ProductFormSubmitGiftAddError: ", error);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);

    this.track("buy_box_gift_error", {
      failed_gift_ids: giftItems.map((item) => item.id).join(","),
      failed_gift_count: giftItems.length,
      error_message: payload.message,
    });

    document.dispatchEvent(
      new CustomEvent("subscriptiongift:error", {
        detail: payload,
      }),
    );
  }

  // "applied", "not_applicable" (on the cart, but Shopify won't apply it yet:
  // functions that need the buyer only decide at checkout) or "missing".
  discountStatus(cart, discountCode) {
    const wanted = String(discountCode).trim().toUpperCase();
    const entry = (cart?.discount_codes || []).find(
      (code) => String(code?.code || "").trim().toUpperCase() === wanted,
    );
    if (!entry) return "missing";
    return entry.applicable ? "applied" : "not_applicable";
  }

  // GA4 event through gtag.js (the store loads gtag directly, no GTM container,
  // so a plain dataLayer object push never reaches GA4). `send_to` keeps the
  // events in GA4 only, not the page's Google Ads tags. Never throws: tracking
  // must not break the add.
  track(name, params = {}) {
    try {
      const payload = {
        ...this.trackingContext,
        ...params,
        send_to: GA4_MEASUREMENT_ID,
      };
      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined || payload[key] === null) {
          delete payload[key];
        } else if (typeof payload[key] === "string") {
          // GA4 drops parameter values over 100 characters.
          payload[key] = payload[key].slice(0, 100);
        }
      });

      if (typeof window.gtag === "function") {
        window.gtag("event", name, payload);
      } else {
        // Same queue gtag.js reads once it loads (it expects an arguments object).
        window.dataLayer = window.dataLayer || [];
        (function () {
          window.dataLayer.push(arguments);
        })("event", name, payload);
      }
    } catch (error) {
      console.error("ProductFormSubmitTrackError: ", error);
    }
  }

  clearCart() {
    return fetch("/cart/clear.js", {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`Cart clear failed: ${response.status}`);
      }
      return response.json();
    });
  }

  applyDiscount(discountCode) {
    const root = window.Shopify?.routes?.root || "/";

    return fetch(`${root}cart/update.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        discount: discountCode,
      }),
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`Discount update failed: ${response.status}`);
      }
      return response.json();
    });
  }

  addToCart(items) {
    return fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ items }),
    }).then(async (response) => {
      if (!response.ok) {
        let description = `Cart add failed: ${response.status}`;
        try {
          const body = await response.json();
          description = body.description || body.message || description;
        } catch (_) {
          // Keep the status-based fallback when the body is not JSON.
        }
        const error = new Error(description);
        error.status = response.status;
        throw error;
      }
      return response.json();
    });
  }

  subscriptionGiftItems() {
    // A checked plan card may carry its own gift tier; it wins over the
    // purchase box's flat gift list.
    const checkedPlan = this.elements.form?.querySelector(
      'input[name="selling_plan"]:checked[data-gift-items]',
    );
    const source = checkedPlan
      ? checkedPlan.dataset.giftItems
      : this.dataset.subscriptionGiftItems;
    if (!source) return [];

    try {
      const items = JSON.parse(source);
      if (!Array.isArray(items)) return [];

      return items
        .map((item) => ({
          id: Number(item.id),
          quantity: Number(item.quantity || 1),
          // Hidden line property the NEW CUSTOMER discount function requires
          // before applying 100% off — marks the line as our auto-added gift.
          properties: { _subscription_gift: "true" },
        }))
        .filter((item) => item.id && item.quantity > 0);
    } catch (error) {
      console.error("ProductFormSubmitGiftItemsError: ", error);
      return [];
    }
  }
}

customElements.define("product-form-submit", ProductFormSubmit);
